<template>
  <Container class="mt-60 xl:mt-90">
    <BackButton
      :to="{ name: 'shop', query: { targetGroup: targetGroup?.name } }"
      :text="$texts('backToShop', 'Zurück zum Shop')"
    />

    <div class="bg-white shadow-lg box-padding mt-30">
      <div class="flex flex-col md:flex-row gap-20 md:gap-30 text-base">
        <div
          class="w-full md:w-200 lg:w-[370px] xl:w-[480px] self-start shrink-0 aspect-square bg-gray-300"
        >
          <MediaImage v-if="image" v-bind="image" :image-style="imageStyle" />
        </div>
        <div class="flex-1 w-full flex flex-col">
          <div class="flex gap-10 mb-10">
            <Tag
              v-if="productType && productType.label"
              :text="productType.label"
              color="bg-white"
            />
            <CommerceTargetGroupIndicator v-bind="targetGroup" />
          </div>
          <h1
            class="text-xl md:text-2xl lg:text-4xl xl:text-6xl font-semibold hyphens-auto"
          >
            {{ title }}
          </h1>

          <p
            v-if="variation?.title"
            class="mb-20 font-normal text-md md:text-lg mt-20"
          >
            {{ variation.title }}
          </p>

          <div v-if="lead" class="mb-20" v-html="lead" />

          <div class="mt-auto">
            <CommerceProductPrice
              v-if="variation?.price"
              v-bind="variation.price"
              :roles="paywall?.requiredRoles"
              :role-based-prices="variation?.roleBasedPrice"
            />

            <div
              v-if="product?.addToCart?.id && isPaywallActivated"
              class="mt-20"
            >
              <AddToCart
                v-bind="product.addToCart"
                :color="targetGroup.color"
                :is-unavailable="variation?.computedAvailability?.isUnavailable"
              />
              <div
                v-if="
                  variation?.computedAvailability?.reason && isPaywallActivated
                "
                class="mt-20"
                v-html="variation?.computedAvailability?.reason"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <PaywallProduct
      v-if="paywall && paywall.showPaywall"
      v-bind="paywall"
      class="my-40 mb-50"
    />
    <CommercePageSection
      v-if="variation && allVariations.length > 1"
      :title="$texts('availableVariations', 'Verfügbare Optionen')"
    >
      <CommercePreparedAttribute
        :attributes="variation.attributes"
        :active-variation-id="variation.id"
        :all-variations="allVariations"
      />
    </CommercePageSection>

    <CommercePageSection :title="$texts('productDetails', 'Produktdetails')">
      <template v-if="description">
        <h3 class="text-lg">
          {{ $texts('productDescription', 'Beschreibung') }}
        </h3>
        <div class="content text-lg" v-html="description" />
      </template>

      <template v-if="files.length">
        <h3 class="text-lg mt-30">
          {{ $texts('productFiles', 'Enthaltene Dateien') }}
        </h3>
        <div class="content text-lg">
          <ul>
            <li v-for="file in files">{{ file }}</li>
          </ul>
        </div>
      </template>

      <h3 class="text-lg mt-30 mb-20">
        {{ $texts('productSpecs', 'Spezifikationen') }}
      </h3>
      <CommerceFeatures
        v-if="variation"
        :variation="variation"
        :show-capacity="showCapacity"
      />
    </CommercePageSection>
  </Container>
</template>

<script lang="ts" setup>
import { CommerceProductPageFragment } from '#graphql-operations'
import { falsy } from '~/helpers/type'

definePageMeta({
  name: 'shop-product',
  drupalRoute: true,
})

const nuxtRoute = useRoute()

const variationId = useQueryString('v')

// Get the data.
const { data: query } = await useAsyncData(
  nuxtRoute.fullPath,
  () =>
    useCachedGraphqlQuery('productRoute', {
      path: nuxtRoute.path,
      variationId: variationId.value || '',
    }).then((v) => v.data),
  {
    watch: [variationId],
  },
)

// Handles redirects and metatags.
await useDrupalRoute<CommerceProductPageFragment>(query.value)
setBreadcrumbLinks(
  query.value && query.value.route && 'breadcrumb' in query.value.route
    ? query.value.route.breadcrumb
    : [],
)

watch(
  () => query.value,
  async () => {
    // Updates metatags after variation changes.
    await useDrupalRoute<CommerceProductPageFragment>(query.value)
  },
)

const product = computed<CommerceProductPageFragment | null>(() => {
  const route = query.value?.route
  if (route && 'entity' in route && route.entity && 'title' in route.entity) {
    return route.entity
  }
  return null
})

const variation = computed(() => product.value?.variation || null)

const allVariations = computed(() =>
  (product.value?.enabledVariations || []).filter(falsy),
)

const description = computed(() => {
  if (product.value && 'description' in product.value) {
    return product.value.description
  }
})

const lead = computed(() => {
  if (product.value && 'lead' in product.value) {
    return product.value.lead
  }
})

const productType = computed(() => {
  if (product.value && 'productType' in product.value) {
    return product.value.productType
  }
})

const title = computed(() => product.value?.title)

const image = computed(() => variation?.value?.image)

const files = computed<string[]>(() => {
  if (variation.value) {
    if ('files' in variation.value) {
      return (
        variation.value.files
          ?.map((v) => {
            const label = v.entity?.label || ''
            const description = v.description || ''
            if (label || description) {
              return description || label
            }
            return null
          })
          .filter(falsy) || []
      )
    }
  }
  return []
})

const paywall = computed(() => {
  if (product.value && 'paywall' in product.value) {
    return product.value.paywall
  }
  return null
})

const targetGroup = computed(() =>
  useCommerceTargetGroup(paywall.value?.requiredRoles),
)

const isPaywallActivated = computed(() => {
  if (product.value && 'paywall' in product.value) {
    return !product.value.paywall.showPaywall
  }
  return false
})

const showCapacity = computed(() =>
  product.value && 'showCapacity' in product.value
    ? product.value.showCapacity
    : false,
)

setLanguageLinksFromRoute(query.value)
useRouteCache((c) => c.setCacheable().setMaxAge(600))
await renderPageDependencies()

defineOptions({
  name: 'PagePharmasuisseShopSlug',
})

const imageStyle = defineImageStyle({
  type: 'sizes',
  aspectRatio: 1,
  sizes: {
    sm: 767,
    md: 200,
    lg: 370,
    xl: 480,
  },
})
</script>
